import { warn } from '@execonline-inc/logging';
import { putQueryParam, toUrlT } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { nothing } from 'maybeasy';
import { Task } from 'taskarian';
import { callApi } from '../../../../../Appy';
import { findLinkT } from '../../../../../LinkyLinky';
import ParticipantsStore from '../../../../../ParticipantsStore';
import ParticipantStore from '../../../../../ParticipantStore';
import ReactionComponent, { RCProps } from '../../../../../ReactionComponent';
import {
  FormOutValues,
  FormTemplate,
  SelectionOption,
  fieldsToTemplate,
  fieldsToValues,
  findFieldT,
} from '../../../../../Resource/FormTemplates/Types';
import { Link } from '../../../../../Resource/Types';
import { fromNullableT } from '../../../../../TaskExt';
import {
  bulkParticipantsLicenseFormResourceDecoder,
  newBulkLicenseAllocationsResourceDecoder,
  submissionResponseDecoder,
} from './Decoders';
import { AddLicenseToParticipantsStore } from './Store';
import {
  FetchingLicenseOptionsErrorType,
  BulkParticipantsLicenseFormResource,
  LoadError,
  NewBulkLicenseAllocationsResource,
  State,
  SubmissionResponse,
  SubmitError,
  invalidFormValues,
  missingTemplate,
} from './Types';

const getForm = callApi(newBulkLicenseAllocationsResourceDecoder, {});

const submitLicenseAllocations = (values: FormOutValues) =>
  callApi(submissionResponseDecoder, values);

const participantStoreToOption = (store: ParticipantStore): SelectionOption => ({
  value: store.resource.payload.id.toString(),
  label: '',
  image: nothing(),
  link: nothing(),
});

const buildLicenseOptionsUrl =
  (participantsStore: ParticipantsStore, licenseAction: string) =>
  (link: Link): Task<FetchingLicenseOptionsErrorType, Link> =>
    toUrlT(link.href)
      .map((url) => {
        const userIds = participantsStore.checkedParticipants.map((p) => p.resource.payload.id);
        let updatedUrl = putQueryParam('user_ids', userIds.join(','))(url);
        updatedUrl = putQueryParam('license_action', licenseAction)(updatedUrl);
        return updatedUrl;
      })
      .map((urlParser) => ({ ...link, href: urlParser.href }));

const populateUserIdsField = (participantsStore: ParticipantsStore) => (userIdsField: any) => {
  userIdsField.value = participantsStore.checkedParticipants.map(participantStoreToOption);
};

const extractFormValues = (template: FormTemplate, userIdsField: any, licenseActionField: any) =>
  fieldsToValues([userIdsField, licenseActionField])
    .mapError(fieldsToTemplate(template))
    .mapError(({ fields }) =>
      invalidFormValues(
        fields.flatMap(({ errors }) => errors),
        fields.filter((f) => f.errors.length > 0),
      ),
    );

const fetchLicenseOptions =
  (participantsStore: ParticipantsStore) =>
  (
    link: Link,
    values: FormOutValues,
  ): Task<FetchingLicenseOptionsErrorType, BulkParticipantsLicenseFormResource> =>
    Task.succeed<FetchingLicenseOptionsErrorType, Link>(link)
      .andThen(buildLicenseOptionsUrl(participantsStore, String(values.license_action || '')))
      .andThen((updatedLink) =>
        callApi(bulkParticipantsLicenseFormResourceDecoder, {})(updatedLink),
      );

const fetchLicenseOptionsForm =
  (participantsStore: ParticipantsStore) =>
  (
    resource: NewBulkLicenseAllocationsResource,
    template: FormTemplate,
  ): Task<FetchingLicenseOptionsErrorType, BulkParticipantsLicenseFormResource> =>
    Task.succeed<FetchingLicenseOptionsErrorType, {}>({})
      .assign(
        'template',
        fromNullableT(template).mapError(() => missingTemplate('self')),
      )
      .assign('link', ({ template }) => findLinkT(template.target, resource.links))
      .assign('userIdsField', ({ template }) =>
        findFieldT('user_ids', 'multiple-selection', template.fields),
      )
      .assign('licenseActionField', ({ template }) =>
        findFieldT('license_action', 'single-selection', template.fields),
      )
      .do(({ userIdsField }) => populateUserIdsField(participantsStore)(userIdsField))
      .andThen(({ userIdsField, licenseActionField, template, link }) =>
        extractFormValues(template, userIdsField, licenseActionField).cata<
          Task<FetchingLicenseOptionsErrorType, BulkParticipantsLicenseFormResource>
        >({
          Ok: (values) => fetchLicenseOptions(participantsStore)(link, values),
          Err: Task.fail,
        }),
      );

const handleSubmitResponse =
  (store: AddLicenseToParticipantsStore) => (response: SubmissionResponse) => {
    switch (response.kind) {
      case 'submission-success':
        store.submitted(response.resource);
        break;
      case 'validation-errors':
        store.licensesReady(response.resource);
        break;
      default:
        assertNever(response);
    }
  };

class Reactions extends ReactionComponent<
  AddLicenseToParticipantsStore,
  State,
  RCProps<AddLicenseToParticipantsStore>
> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store } = this.props;
    switch (state.kind) {
      case 'waiting':
        break;
      case 'loading':
        Task.succeed<LoadError, Link>(state.link)
          .andThen(getForm)
          .fork(store.loadingError, store.initialReady);
        break;
      case 'loading-error':
        warn('Could not load license allocations form', JSON.stringify(state.error));
        break;
      case 'initial-ready':
        break;
      case 'fetching-license-options':
        fetchLicenseOptionsForm(state.participantsStore)(state.resource, state.template).fork(
          store.fetchingLicenseOptionsError,
          store.licensesReady,
        );
        break;
      case 'fetching-license-options-error':
        warn('Could not fetch license options', JSON.stringify(state.error));
        break;
      case 'licenses-ready':
        break;
      case 'submitting':
        Task.succeed<SubmitError, ReadonlyArray<Link>>(state.resource.links)
          .andThen(findLinkT('create'))
          .andThen(submitLicenseAllocations(state.values))
          .fork(store.submittingError, handleSubmitResponse(store));
        break;
      case 'submitting-error':
        warn('Could not submit license allocations form', JSON.stringify(state.error));
        break;
      case 'submitted':
        break;
      default:
        assertNever(state);
    }
  };
}

export default Reactions;
